import { Stack, Title } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router";
import { IconButton } from "../../../mantine/components/IconButton/IconButton";
import type { Delivery, Person, Site } from "../../../utils/backend-types";
import { NoDataMessage } from "../../BuildingBlocks/NoDataMessage/NoDataMessage";
import type { Contract } from "../DeliveryOverview.types";
import { ContractDeliveryTable } from "./ContractDeliveryTable/ContractDeliveryTable";
import {
  ContractModal,
  type ContractModalProps
} from "./ContractModal/ContractModal";
import {
  DeliveryModal,
  type DeliveryModalProps
} from "./DeliveryModal/DeliveryModal";
import { NewContractDecisionModal } from "./NewContractDecisionModal/NewContractDecisionModal";

const newPath = `neu`;

export type ContractDeliveriesProps = {
  projectId: string;
  variantId: number;
  contracts: Array<Contract>;
  persons: Array<Person>;
  deliveries: Array<Delivery>;
  onContractsUpdated: () => void;
  onDeliveriesUpdated: () => void;
  sites: Array<Site>;
};

function ContractDeliveries({
  projectId,
  variantId,
  contracts: initialContracts,
  persons,
  deliveries: initialDeliveries,
  onContractsUpdated,
  onDeliveriesUpdated,
  sites
}: ContractDeliveriesProps) {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState(initialContracts);
  const [deliveries, setDeliveries] = useState(initialDeliveries);

  const noContracts = !contracts || contracts.length === 0;
  useEffect(() => {
    setContracts(initialContracts);
  }, [initialContracts]);
  useEffect(() => {
    setDeliveries(initialDeliveries);
  }, [initialDeliveries]);

  function handleSubmitNewDelivery() {
    onDeliveriesUpdated();
    handleCloseDeliveryModal();
  }

  function handleCloseDeliveryModal() {
    navigate(".");
  }

  function handleEditDelivery(updatedDelivery: Delivery) {
    const updatedDeliveries = [...deliveries];
    const index = deliveries.findIndex(
      (delivery) => delivery.id === updatedDelivery.id
    );

    if (index !== -1) {
      updatedDeliveries[index] = updatedDelivery;
    }

    setDeliveries(updatedDeliveries);
    onDeliveriesUpdated();
    handleCloseDeliveryModal();
  }

  function handleSubmitNewOrEditContract() {
    onContractsUpdated();
    onDeliveriesUpdated();
    handleCloseContractModal();
  }

  function handleCloseContractModal() {
    navigate(".");
  }

  function handleContractsDeleted(ids: Array<string>) {
    const newContracts = contracts.filter(
      (contract) => !ids.includes(contract.id)
    );

    setContracts(newContracts);
    onContractsUpdated();
  }

  const ElementDeliveryModalWithDeliveryId = (
    <DeliveryModalWithDeliveryId
      projectId={projectId}
      onClose={handleCloseDeliveryModal}
      onSubmit={handleEditDelivery}
    />
  );

  const ElementContractModalWithContractId = (
    <ContractModalWithContractId
      projectId={projectId}
      variantId={variantId}
      onClose={handleCloseContractModal}
      onSubmit={handleSubmitNewOrEditContract}
    />
  );

  return (
    <div className="ContractDeliveries">
      <Routes>
        <Route
          element={
            <DeliveryModal
              projectId={projectId}
              onClose={handleCloseDeliveryModal}
              onSubmit={handleSubmitNewDelivery}
            />
          }
          path="neue-lieferung"
        />
        <Route
          element={ElementDeliveryModalWithDeliveryId}
          path="lieferung/:deliveryId"
        >
          <Route element={ElementDeliveryModalWithDeliveryId} index />
          <Route element={ElementDeliveryModalWithDeliveryId} path="*" />
        </Route>
        <Route
          element={
            <NewContractDecisionModal
              projectId={projectId}
              sites={sites.filter((site) => site.variant === variantId)}
              templateContracts={contracts.filter(
                (contract) => contract.is_template
              )}
              variantId={variantId}
              onClose={handleCloseContractModal}
              onSubmit={handleSubmitNewOrEditContract}
            />
          }
          path="neu"
        />
        <Route element={ElementContractModalWithContractId} path=":contractId">
          <Route element={ElementContractModalWithContractId} index />
          <Route element={ElementContractModalWithContractId} path="*" />
        </Route>
      </Routes>
      {noContracts ? (
        <NoContractsComponent />
      ) : (
        <div className="contract-deliveries-overview">
          <ContractDeliveryTable
            contracts={contracts}
            deliveries={deliveries}
            persons={persons}
            projectId={projectId}
            sites={sites}
            onContractsUpdated={onContractsUpdated}
            onDataDeleted={handleContractsDeleted}
            onDeliveriesUpdated={onDeliveriesUpdated}
          />
        </div>
      )}
    </div>
  );
}

function NoContractsComponent() {
  return (
    <Stack align="center" gap={0} py={60}>
      <Title fw={600} mb={0} size="h3">
        Fügen Sie einen Vertrag hinzu
      </Title>
      <NoDataMessage
        c="neutral"
        fw={600}
        message={
          'Es sind derzeit keine Verträge vorhanden. Klicken Sie auf den Button "Vertrag hinzufügen", um einen hinzuzufügen.'
        }
      />
      <IconButton color="primary" component={Link} icon={IconPlus} to={newPath}>
        Vertrag hinzufügen
      </IconButton>
    </Stack>
  );
}

function DeliveryModalWithDeliveryId(
  props: Omit<DeliveryModalProps, "deliveryId">
) {
  const { deliveryId } = useParams();
  return <DeliveryModal deliveryId={deliveryId} {...props} />;
}

function ContractModalWithContractId(
  props: Omit<ContractModalProps, "contractId">
) {
  const { contractId } = useParams();
  return <ContractModal contractId={contractId} {...props} />;
}

export { ContractDeliveries };
